// @ts-nocheck
import React, { useState, useEffect, useMemo, useRef } from "react";
import { PageEmptyState, PageHeader } from "../components/AssetBreadCrumb";
import { AssetGroupFolder, AssetGroupFolderSkeleton, NoAssetGroup } from "../components/AssetGroupFolder";
import { ReactComponent as PlusIcon } from "../assets/icons/plus.svg";
import { ReactComponent as SearchIcon } from "../assets/icons/search-sm.svg";
import { useDispatch, useSelector } from "react-redux";
import { setModalState, modalViewsConst } from "../store/modalSlice";
import { fetchAuthSession } from "aws-amplify/auth";
import { checkAssetLimit } from "../api/stripeSubscription";
import { updateCogId } from "../api/awsLamba";
import { getOwnerFromId, filterAssetGroups } from "../util/utils";
import { greetingForNow, firstNameOf } from "../utils/dateUtils";
import { getMyOrganizations, fetchSubscriptionStatus, selectSubscriptionStatus } from "../store/userSlice";
import { selectTotalAssetCount } from "../store/assetsSlice";
import { useGroupImagePreloader } from "../hooks/useImagePreloader";
import { useDismissOnOutsideOrEscape } from "../hooks/useOutsideClick";

import FilterBar, { FilterBarSkeleton } from "../components/ReusableComponents/FilterBar";
import { Skeleton, SkeletonGroup } from "../components/Skeleton";

const Home = () => {
  const dispatch = useDispatch();
  const [searchValue, setSearchValue] = useState("");
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const addMenuRef = useRef<HTMLDivElement | null>(null);
  useDismissOnOutsideOrEscape(addMenuOpen, addMenuRef, () => setAddMenuOpen(false));
  const [ownerFilter, setOwnerFilter] = useState("");
  const [orgFilter, setOrgFilter] = useState("");
  const [ownerNames, setOwnerNames] = useState({});
  const { user, assetGroups: assetGroupState } = useSelector(({ assetGroups, user }) => ({
    assetGroups,
    user,
  }));

  const totalAssetCount = useSelector(selectTotalAssetCount);
  const subscriptionStatus = useSelector(selectSubscriptionStatus);

  // Preload group images for better caching
  useGroupImagePreloader(assetGroupState.groups);

  useEffect(() => {
    if (
      user?.permissionDetails?.username &&
      !user.permissionDetails.cognitoIdentityId
    ) {
      // Craig update user info in redux here
      updateCogId();
    }
  }, [user.permissionDetails]);

  useEffect(() => {
    if (!user.organizations?.loaded) {
      dispatch(getMyOrganizations());
    }
  }, [dispatch, user.organizations?.loaded]);

  // Prefetch the subscription tier on mount so the asset-limit check is instant on click (no fetch lag). Shared, deduped Redux cache — fail open (null) while loading or after a failed read.
  useEffect(() => {
    dispatch(fetchSubscriptionStatus());
  }, [dispatch]);

  // Memoized so the filter and option lists below can depend on it without re-running every render
  const organizations = useMemo(
    () => (user.organizations?.loaded ? user.organizations?.data || {} : {}),
    [user.organizations]
  );

  useEffect(() => {
    const fetchOwnerNames = async () => {
      if (assetGroupState.groups) {
        const ownerNamesMap = {};

        for (const groupId of Object.keys(assetGroupState.groups)) {
          const group = assetGroupState.groups[groupId];
          if (group.owner && !ownerNamesMap[group.owner]) {
            try {
              const ownerDetails = await getOwnerFromId(group.owner);
              ownerNamesMap[group.owner] = ownerDetails?.name || ownerDetails?.username || "Unknown User";
            } catch (error) {
              console.error("Error fetching owner details:", error);
              ownerNamesMap[group.owner] = "Unknown User";
            }
          }
        }

        setOwnerNames(ownerNamesMap);
      }
    };

    fetchOwnerNames();
  }, [assetGroupState.groups]);

  const filteredGroups = useMemo(
    () =>
      filterAssetGroups({
        groups: assetGroupState.groups,
        searchTerm: searchValue,
        ownerId: ownerFilter,
        organizationId: orgFilter,
        ownerNames,
        organizations,
      }),
    [assetGroupState.groups, searchValue, ownerFilter, orgFilter, ownerNames, organizations]
  );

  // Select options come from the groups themselves, so a filter never offers an empty result.
  const ownerOptions = useMemo(() => {
    const ids = new Set(
      Object.values(assetGroupState.groups || {}).map((g) => g.owner).filter(Boolean)
    );
    return [...ids]
      .map((id) => ({ value: id, label: ownerNames[id] || "Unknown User" }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [assetGroupState.groups, ownerNames]);

  const orgOptions = useMemo(() => {
    const ids = new Set(
      Object.values(assetGroupState.groups || {}).map((g) => g.organizationId).filter(Boolean)
    );
    return [...ids]
      .map((id) => ({ value: id, label: organizations[id]?.name || "Unknown organization" }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [assetGroupState.groups, organizations]);

  const handleAddAssetGroup = async () => {
    const authSession = await fetchAuthSession();
    const groups =
      authSession?.tokens?.idToken?.payload?.["cognito:groups"] ?? [];
    const assetGroupCount = user.permissionDetails?.assetGroupCount ?? 1; //1 is min

    if (
      groups?.includes("Admin") ||
      (assetGroupState?.groups &&
        Object.keys(assetGroupState?.groups).length < assetGroupCount)
    ) {
      dispatch(
        setModalState({
          modalOpen: true,
          modalView: modalViewsConst.addAssetGroup,
          properties: null,
        })
      );
    } else {
      dispatch(
        setModalState({
          modalOpen: true,
          modalView: modalViewsConst.notification,
          properties: {
            __modalSize: "sm",
            header: "Quick Note",
            details: `Asset Groups are currently being capped at ${assetGroupCount} per user. Please reach out to support@erode.ai for any inquiries`,
          },
        })
      );
    }
  };

  const handleAddAsset = async () => {
    const authSession = await fetchAuthSession();
    const groups = authSession?.tokens?.idToken?.payload?.["cognito:groups"];
    const isAdmin = groups?.includes("Admin");

    // Admins are exempt; everyone else is capped by their subscription tier (same check as chat).
    if (!isAdmin) {
      // Uses the prefetched subscriptionStatus — null while loading or on fetch failure means fail open.
      if (subscriptionStatus) {
        const check = checkAssetLimit(subscriptionStatus, totalAssetCount, 1);
        if (!check.allowed) {
          dispatch(
            setModalState({
              modalOpen: true,
              modalView: modalViewsConst.assetLimitReached,
              properties: {
                __modalSize: "xs",
                tier: check.tier,
                limit: check.limit,
                currentCount: check.currentCount,
                source: "home_add_asset",
              },
            })
          );
          return;
        }
      }
    }

    if (Object.keys(assetGroupState.groups).length === 0) {
      dispatch(
        setModalState({
          modalOpen: true,
          modalView: modalViewsConst.addGroupFirst,
          properties: null,
        })
      );
    } else {
      dispatch(
        setModalState({
          modalOpen: true,
          modalView: modalViewsConst.addAssetNEW,
          properties: { __modalSize: "md" },
        })
      );
    }
  };

  const headerActions = (
    <>
      <button
        type="button"
        onClick={handleAddAssetGroup}
        data-testid="home-add-group"
        className="eai-btn-outline hidden sm:inline-flex"
      >
        <PlusIcon className="h-4 w-4 [&_path]:stroke-current" />
        New Group
      </button>
      <button
        type="button"
        onClick={handleAddAsset}
        data-testid="home-add-asset"
        className="eai-btn-cta hidden sm:inline-flex"
      >
        <PlusIcon className="h-4 w-4 [&_path]:stroke-current" />
        New Asset
      </button>
      {/* Phone: both creates share one + beside the title; a plain popover (Tab reaches the items), not an ARIA menu without arrow keys */}
      <div className="relative sm:hidden" ref={addMenuRef}>
        <button
          type="button"
          onClick={() => setAddMenuOpen((open) => !open)}
          aria-haspopup="true"
          aria-expanded={addMenuOpen}
          aria-controls="home-add-menu"
          aria-label="Create a group or asset"
          title="Create"
          data-testid="home-add-menu"
          className="eai-btn-cta eai-btn-icon-mobile"
        >
          <PlusIcon className="h-4 w-4 [&_path]:stroke-current" />
        </button>
        {addMenuOpen && (
          <div id="home-add-menu" className="eai-card absolute right-0 top-full z-30 mt-1.5 w-44 p-1">
            <button
              type="button"
              onClick={() => {
                setAddMenuOpen(false);
                handleAddAssetGroup();
              }}
              className="eai-menu-item flex w-full items-center gap-2"
            >
              <PlusIcon className="h-3.5 w-3.5 shrink-0 [&_path]:stroke-current" />
              New Group
            </button>
            <button
              type="button"
              onClick={() => {
                setAddMenuOpen(false);
                handleAddAsset();
              }}
              className="eai-menu-item flex w-full items-center gap-2"
            >
              <PlusIcon className="h-3.5 w-3.5 shrink-0 [&_path]:stroke-current" />
              New Asset
            </button>
          </div>
        )}
      </div>
    </>
  );

  const greeting = `Good ${greetingForNow()}${
    firstNameOf(user?.permissionDetails?.name) ? `, ${firstNameOf(user.permissionDetails.name)}` : ""
  }`;

  const renderOptions = () => {
    if (Object.keys(filteredGroups).length === 0) {
      return (
        <PageEmptyState
          className="w-full"
          icon={<SearchIcon className="h-6 w-6" />}
          title="No groups found"
          description="Try a different search, or clear the filters"
        />
      );
    }

    return Object.keys(filteredGroups).map((groupId) => (
      <AssetGroupFolder key={groupId} assetGroup={filteredGroups[groupId]} />
    ));
  };

  // Placeholders in the real layout rather than a spinner, so the page lands in place
  if (assetGroupState.loading !== "success") {
    return (
      <div className="w-full">
        <PageHeader eyebrow="Asset Groups" title={greeting} />
        <FilterBarSkeleton filters={2} />
        <div className="eai-section-label flex items-center gap-2.5">
          <Skeleton className="h-3 w-20" />
          <span className="h-px flex-1 bg-eai-border" />
        </div>
        <SkeletonGroup count={6} label="Loading groups" className="eai-card-grid">
          <AssetGroupFolderSkeleton />
        </SkeletonGroup>
      </div>
    );
  }

  // Safety net for anyone who reaches Home with no asset groups (e.g. an org member with none yet). Non-org users are redirected to AI Analyst in VerticalNav before they get here, but this ensures Home is never a bare, empty list — it nudges the user to create their first asset group instead.
  const hasNoAssetGroups = Object.keys(assetGroupState?.groups || {}).length === 0;

  if (hasNoAssetGroups) {
    return (
      <div className="w-full">
        <PageHeader eyebrow="Asset Groups" title={greeting} />
        <div className="eai-panel flex items-center justify-center py-12">
          <NoAssetGroup handleAddAssetGroup={handleAddAssetGroup} />
        </div>
      </div>
    );
  }

  const totalGroups = Object.keys(assetGroupState.groups).length;
  const shownGroups = Object.keys(filteredGroups).length;
  const isFiltered = Boolean(searchValue.trim() || ownerFilter || orgFilter);

  return (
    <div className="w-full">
      <PageHeader eyebrow="Asset Groups" title={greeting} action={headerActions} />

      <FilterBar
        sticky
        searchId="home-search"
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        searchPlaceholder="Search by name, owner, or organization"
        filters={[
          {
            id: "home-owner",
            label: "Owner",
            value: ownerFilter,
            onChange: setOwnerFilter,
            options: ownerOptions,
            allLabel: "Anyone",
          },
          {
            id: "home-org",
            label: "Organization",
            value: orgFilter,
            onChange: setOrgFilter,
            options: orgOptions,
            allLabel: "All",
          },
        ]}
        onClear={() => {
          setSearchValue("");
          setOwnerFilter("");
          setOrgFilter("");
        }}
        isFiltered={isFiltered}
      />

      <div className="eai-section-label flex items-center gap-2.5">
        <span className="eai-eyebrow">
          {isFiltered ? `${shownGroups} of ${totalGroups} Groups` : `${totalGroups} Groups`}
        </span>
        <span className="h-px flex-1 bg-eai-border" />
      </div>

      <div className="eai-card-grid">
        {renderOptions()}
      </div>
    </div>
  );
};

export default Home;
